import { canRoles, PostalAddressDetail, IntervenerSearcher, IntervenerForm, fields,
    formatter, checkIfErrors } from './_helpers';
import formatArrayData from '@/mixins/formatArrayData';
import keyed from '@/mixins/keyed';
import validations from '@/mixins/validations';
import FormWatcher from '@/inheritance/form-watcher';

export default {
    name: 'RelatedInterveners',
    extends: FormWatcher,
    mixins: [formatArrayData, keyed, validations],
    components: {
        PostalAddressDetail,
        IntervenerSearcher,
        IntervenerForm
    },

    created() {
        this.fields = fields;

        const storedComponentData = this.getStoredComponentData();
        if (storedComponentData && Object.keys(storedComponentData).length) {
            Object.entries(storedComponentData).forEach(([key, value]) => this[key] = value);
            this.$nextTick(() => {
                this.$emit('change');
            });
        }
        this.fetchData();
    },

    /**
     * @override
     */
    data() {
        return {
            uuid: null,
            data: [],
            editing: false,
            uuid_account: null,
            fields: [],
            createIntervener: false,
            removedItems: [],
            selectedRoles: [],
            selectedMailing: [],
            selectedMailingType: [],
            formWatcherProperties: ['formattedData', 'modifiedData', 'data', 'selectedRoles', 'selectedMailing', 'selectedMailingType']
        };
    },
    props: {
        type: {
            type: String,
            required: true
        },
        limit: {
            type: Number,
            required: false
        },
        id: {
            type: String,
            required: false
        },
        creation: {
            type: Boolean,
            required: false,
            default: false
        },
        accountType: {
            type: String,
            required: false,
            default: null
        },
        intervenerType: {
            type: String,
            required: false,
            default: null
        },
        accountStatus: {
            type: String,
            required: false,
            default: null
        }
    },
    methods: {
        /**
         * Obtenemos los intervinientes relacionados con la cuenta/operación
         */
        fetchData() {
            this.uuid = this.id || this.$route.params.uuid;
            const cfg = {};
            cfg.params = {};
            cfg.params.$expand = 'postal_address, postal_addresses, account_intervener_role';
            if (this.limit) {
                cfg.params.$limit = this.limit;
            }

            switch (this.type) {
                case 'operation':
                    this.$operationsService
                        .getOperationsByUUId(this.uuid)
                        .then(res => {
                            this.uuid_account = res.data.operation.account_id;
                            return this.$operationsService.getOperationInterveners(this.uuid, cfg);
                        })
                        .then(res => {
                            this.data = res.data.operations_interveners || [];
                            this.formatData();
                        });
                    break;
                    case 'account':
                        this.uuid_account = this.uuid;
                        this.$accountsService.getAccountInterveners(this.uuid, cfg).then(res => {
                            if (res.data.accounts_intervener) {
                                const holder = [];
                                const interveners = [];
                                res.data.accounts_intervener.forEach( intervener => {
                                    const isHolder = intervener.account_intervener_role.some( rol => ['TITULAR', 'NUDO_PROPIETARIO'].includes(rol));
                                    if (isHolder) {
                                        holder.push(intervener);
                                    } else {
                                        interveners.push(intervener);
                                    }
                                });
                                this.data = [...holder, ...interveners];
                            } else {
                                this.data = res.data.accounts_intervener || [];
                            }
                            this.formatData();
                        });
                    break;
                default:
                    return;
            }
        },

        /**
         * @override
         */
        constructFullName(intervener) {
            let name = intervener.name ? `${intervener.name}` : '';
            name += intervener.lastname_1 ? ` ${intervener.lastname_1}` : '';
            name += intervener.lastname_2 ? ` ${intervener.lastname_2}` : '';
            name = name.trim();
            return name;
        },

        /**
         * Validate relationships
         * @return {Boolean} if relationships are valids
         */
        validateData() {
            this.cleanErrors();

            return Promise.all(
                this.formattedData.map(
                    (intervener, idx) =>
                        new Promise(resolve => {
                            const full_name = `${intervener.name || ''} ${intervener.lastname_1 ||
                                ''} ${intervener.lastname_2 || ''}`;

                            if (
                                !intervener.account_intervener_role ||
                                !intervener.account_intervener_role.length ||
                                intervener.account_intervener_role.length < 1 ||
                                !intervener.account_intervener_role[0]
                            ) {
                                this.$refs[`account_intervener_role-${this.key}-${idx}`].$el.classList.add(
                                    'creation-error'
                                );
                                throw new Error(`El interviniente ${full_name} debe tener un rol asignado`);
                            }
                            if (intervener.is_minor && intervener.account_intervener_role.includes('TUTOR')) {
                                this.$refs[`account_intervener_role-${this.key}-${idx}`].$el.classList.add(
                                    'creation-error'
                                );
                                throw new Error(`${full_name} no puede ser tutor ya que es menor de edad.`);
                            }
                            if (!intervener['mailing']) {
                                this.$refs[`mailing-${this.key}-${idx}`].$el.classList.add('creation-error');
                                throw new Error(`Debe seleccionar un medio de correspondencia para ${full_name}`);
                            }

                            resolve(true);
                        })
                )
            )
                .then(() => {
                    try {
                        checkIfErrors(this.formattedData, this.accountType);
                        return true;
                    } catch (error) {
                        throw error;
                    }
                })
                .catch(err => {
                    this.$toast.error('Errores en formulario - "Intervinientes de la cuenta"', err.message);
                    return false;
                });
        },

        /**
         * Función genérica para guardar los elementos editados
         * @param {*} idx indice
         * @param {*} field Campo editado
         * @param {*} val Valor
         */
        setValueEdited(idx, field, val) {
            const _m = this.modifiedData[idx];
            switch (field) {
                case 'account_intervener_role':
                    _m[field] =
                        val[0] === 'remove' ? _m[field].filter(x => x !== val[1]) : [...(_m[field] || []), val[1]];
                    this.$nextTick(() => {
                        this.$emit('change');
                    });
                    this.selectedRoles[idx] = _m[field];
                    break;
                case 'mailing':
                    _m['mailing_type'] = val.key === 'EMAIL' ? 'EMAIL' : 'POSTAL';
                    _m['uuid_postal_address'] = _m['mailing_type'] === 'POSTAL' ? val.value.uuid : undefined;
                    this.selectedMailing[idx] = val;
                    this.selectedMailingType[idx] = _m['mailing_type'];
                    break;
                case 'holding_percentage':
                    _m[field] = parseFloat(val);
                    break;
                    
                default:
                    break;
            }
        },
        /**
         * Set row class
         * @param {Object} item item to set class
         * @param {String} type type of item
         * @return {String} class
         */
        setRowClass() {
            return '';
        },
        /**
         * Emit add interveners on creation
         */
        addIntervenerOnCreation() {
            this.addIntervenerClick = true;
            this.$emit('addIntervenerOnCreation');
        },
        /**
         * Navigate to new intervener
         */
        gotoNewIntervener() {
            const _route = {
                name: 'interveners_add',
                params: [],
                query: {
                    uuid_account: this.uuid_account
                }
            };
            const item = {
                route: _route,
                name: 'Nuevo interviniente',
                active: true
            };
            this.$store.dispatch('yuriMenuStore/disableItemsMenu').then(() => {
                this.$store.dispatch('yuriMenuStore/upsertItem', item).then(() => {
                    this.$router.replace(item.route);
                });
                this.$store.dispatch('yuriMenuStore/setActiveItem', item);
            });
        },

        /**
         * Añadir interviniente seleccionado al listado
         * @param {*} selection Interviniente seleccionado
         */
        onSelect(selection) {
            if (!this.data) {
                this.data = [];
            }
            if (this.accountType === 'J' && selection.personalData.intervener_type === 'J') {
                if (this.data.find(x => x.personalData.intervener_type === 'J')) {
                    this.$toast.error(
                        'Intervinientes de la cuenta',
                        'No se pueden añadir dos personas jurídicas en este tipo de cuenta.'
                    );
                } else {
                    this.addIntervenerToList(selection);
                }
            } else {
                this.addIntervenerToList(selection);
            }
        },

        /**
         * Añadir interviniente creado al listado
         * @param {*} intervener Interviniente creado
         */
        addIntervenerToList(intervener) {
            if (!this.data.filter(x => x.uuid === intervener.uuid).length) {
                this.data.push(intervener);
                this.formatData();
            }
        },

        addIntervener(intervener) {
            if (!this.data) {
                this.data = [];
            }

            this.data.push(intervener);
            this.createIntervener = false;
            this.formatData();
        },

        onEditRoles() {
            this.editing = true;
        },

        /**
         * Añade un rol al interviniente
         * @param {*} $event datos el evento
         * @param {*} row Fila del interviniente
         */
        submit() {
            this.fetchValidatedData()
                .then(vd =>
                    Promise.all([
                        Promise.resolve(vd),
                        ...this.removedItems.map(i => {
                            return this.$accountsService.deleteAccountInterveners(this.uuid_account, i.uuid);
                        })
                    ])
                )
                .then(res => {
                    this.removedItems = [];
                    return res[0];
                })
                .then(vd => {
                    const interveners = vd.map(x => {
                        return {
                            personal_data: x.personalData,
                            contacts: x.contactData,
                            postal_address: x.postalData,
                            fiscal_address: x.fiscalData,
                            crs: x.crsData,
                            fatca: x.fatcaData,
                            business: x.registralesData,
                            account_relationship: {
                                account_intervener_role: x.account_intervener_role,
                                mailing_type: x.mailing_type,
                                holding_percentage: x.holding_percentage,
                                uuid_postal_address: x.uuid_postal_address
                            },
                            href: x.href
                        };
                    });
                    return this.$accountsService.createBulk(this.uuid_account, {
                            interveners,
                            resend_to_rd: true
                    });
                })
                .then(
                    () => {
                        this.editing = false;
                        this.fetchData();
                        this.$toast.success(`Guardado`, `Datos modificados con éxito.`);
                    }
                );
        },

        cancel() {
            this.removedItems.forEach(x => {
                this.data.push(x);
            });
            this.removedItems = [];
            this.fetchData();
            this.editing = false;
        },

        /**
         * Elimina relación
         * @param {*} row interviniente
         */
        removeRelation(row) {
            this.data.splice(row.index, 1);
            this.selectedRoles.splice(row.index, 1);
            this.selectedMailing.splice(row.index, 1);
            if (!this.creation) {
                this.removedItems.push(row.item);
            }
            this.$nextTick(() => {
                this.$emit('change');
            });
            this.formatData();
        },

        /**
         * Crea la lista de roles posibles
         * @param {*} row Intervinientes
         * @return {*} Listado de roles
         */
        availableRoleTypes(row) {
            const me = row.item;
            const others = this.formattedData.filter(x => x.document_id !== row.item.document_id);
            const roles = [];
            canRoles(this.accountType, others, me).forEach(rol => {
                if (rol !== undefined) {
                    roles.push(rol);
                }
            });
            return row.item.account_intervener_role
                ? [...roles, ...row.item.account_intervener_role].reduce((acc, x) => {
                    if (!acc.includes(x)) {
                        acc.push(x);
                    }
                    return acc;
                }, [])
                : roles;
        },
        /**
         * Devuelve si el interviniente es titular o nudo propietario
         * @param {*} intervener interviniente
         * @return {Boolean}
         */

        isHolder(intervener) {
            if (!intervener.account_intervener_role) {
                return;
            }
            return (
                intervener.account_intervener_role.includes('TITULAR') ||
                intervener.account_intervener_role.includes('NUDO_PROPIETARIO')
            );
        },

        mailingOptions(intervener) {
            const options = [];
            if (intervener.email) {
                options.push({
                    mailing_type: 'EMAIL',
                    options: [{ key: 'EMAIL', value: intervener.email }]
                });
            }
            if (intervener.postal_addresses) {
                options.push({
                    mailing_type: 'POSTAL',
                    options: [...intervener.postal_addresses.map(value => ({ key: `POSTAL-${value.uuid}`, value }))]
                });
            }
            return options;
        },

        formatData() {
            if (!Array.isArray(this.data)) {
                return;
            }
            this.formattedData = formatter(this.data, this.selectedMailingType, this.selectedMailing);
            this.modifiedData = this.formattedData.map(x => ({
                is_minor: x.is_minor,
                account_intervener_role: x.account_intervener_role,
                holding_percentage: x.holding_percentage,
                mailing_type: x.mailing_type,
                uuid_postal_address: x.postal_address ? x.postal_address.uuid : undefined
            }));
        },

        canEditRoleAndMailing(item) {
            if (!this.editing && !this.creation) {
                return false;
            } else if (!item.account_intervener_role) {
                return true;
            } else if (item.account_intervener_role.includes('APORTANTE_CONYUGE') || item.account_intervener_role.includes('APORTANTE_FAMILIAR')) {
                return false;
            } else {
                return true;
            }
        },

        textToRoles(item, role) {
            if (role === 'TITULAR' && item.intervener_status === 'BLOQUEADO') {
                return 'Titular Bloqueado';
            }
            return this.$t(`interveners.role.${role}`);

        },

        canEditHoldingPercentage(intervener) {
            const roles = intervener.account_intervener_role || [];
            if (intervener.intervener_type === 'J') return roles.includes('TITULAR');
            if (intervener.intervener_type === 'F') return roles.includes('TITULAR') || roles.includes('NUDO_PROPIETARIO');
            return false;
        }
        
    }
};
